import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { DollarSign, Loader2, Sparkles, Save, FlaskConical, RefreshCw, Trophy } from "lucide-react";
import { toast } from "sonner";

const CURRENCIES = ["usd", "aud", "eur", "gbp", "cad", "nzd"];
const SYMBOLS = { usd: "$", aud: "A$", eur: "€", gbp: "£", cad: "C$", nzd: "NZ$" };
const fmtMoney = (price, currency) =>
  `${SYMBOLS[currency] || "$"}${Number(price).toFixed(Number.isInteger(Number(price)) ? 0 : 2)}`;
const pct = (num, den) => (den > 0 ? ((100 * num) / den).toFixed(1) + "%" : "—");

const TabButton = ({ active, onClick, icon: Icon, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2 text-sm font-medium border-b-2 inline-flex items-center gap-2 transition-colors ${
      active
        ? "text-white border-[#002FA7]"
        : "text-zinc-500 border-transparent hover:text-zinc-300"
    }`}
  >
    <Icon className="w-4 h-4" /> {children}
  </button>
);

// ---------- Sub-component: Base pricing form ----------
const BasePricingForm = ({ data, onSaved }) => {
  const [form, setForm] = useState({
    price: data.price,
    currency: data.currency,
    introductory: data.introductory,
    introductory_label: data.introductory_label || "",
    introductory_note: data.introductory_note || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    if (form.price <= 0) { toast.error("Price must be greater than zero"); return; }
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/subscription-pricing`, form);
      onSaved(r.data);
      toast.success("Pricing updated — change is live immediately for new sign-ups.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="bg-zinc-900 border border-zinc-800 rounded p-6 space-y-5" data-testid="admin-base-pricing-form">
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2">
          <label className="text-xs uppercase tracking-wide text-zinc-400">Monthly price *</label>
          <div className="mt-1 flex items-center">
            <span className="px-3 py-2 text-sm text-zinc-400 bg-black/40 border border-r-0 border-zinc-700 rounded-l">
              {SYMBOLS[form.currency] || "$"}
            </span>
            <input
              type="number" step="0.01" min="0.01" max="9999" required
              value={form.price}
              onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
              className="flex-1 px-3 py-2 bg-black/40 border border-zinc-700 text-zinc-100 font-mono text-lg tabular-nums rounded-r"
              data-testid="admin-pricing-price-input"
            />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-zinc-400">Currency</label>
          <select
            value={form.currency}
            onChange={(e) => setForm({ ...form, currency: e.target.value })}
            className="w-full mt-1 px-3 py-2 bg-black/40 border border-zinc-700 rounded text-zinc-100"
            data-testid="admin-pricing-currency"
          >
            {CURRENCIES.map((c) => <option key={c} value={c}>{c.toUpperCase()}</option>)}
          </select>
        </div>
      </div>

      <div className="border-t border-zinc-800 pt-5">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.introductory}
            onChange={(e) => setForm({ ...form, introductory: e.target.checked })}
            className="w-4 h-4 accent-[#002FA7]"
            data-testid="admin-pricing-intro-checkbox"
          />
          <span className="text-sm font-medium text-zinc-100 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-[#FBBF24]" /> Show "introductory price" badge on the homepage
          </span>
        </label>
        <p className="text-xs text-zinc-500 mt-1 ml-6">
          Adds a friendly badge so prospective customers know this is a launch price that may rise.
        </p>
      </div>

      {form.introductory && (
        <div className="space-y-3 pl-6 border-l-2 border-amber-700/50">
          <div>
            <label className="text-xs uppercase tracking-wide text-zinc-400">Badge label</label>
            <input
              value={form.introductory_label}
              onChange={(e) => setForm({ ...form, introductory_label: e.target.value })}
              maxLength={80}
              placeholder="e.g. Introductory price"
              className="w-full mt-1 px-3 py-2 bg-black/40 border border-zinc-700 rounded text-zinc-100"
              data-testid="admin-pricing-intro-label"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wide text-zinc-400">Note shown under the price</label>
            <textarea
              value={form.introductory_note}
              onChange={(e) => setForm({ ...form, introductory_note: e.target.value })}
              maxLength={400} rows={3}
              placeholder="e.g. Locked-in launch pricing for early customers — price may rise for new sign-ups."
              className="w-full mt-1 px-3 py-2 bg-black/40 border border-zinc-700 rounded text-zinc-100 text-sm"
              data-testid="admin-pricing-intro-note"
            />
            <p className="text-[10px] text-zinc-500 mt-0.5 tabular-nums">{form.introductory_note.length}/400</p>
          </div>
        </div>
      )}

      <button
        type="submit" disabled={saving}
        className="w-full py-2.5 bg-[#002FA7] hover:bg-[#00227A] text-white font-medium rounded inline-flex items-center justify-center gap-2 disabled:opacity-60"
        data-testid="admin-pricing-save"
      >
        {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save pricing</>}
      </button>

      <div className="text-[11px] text-zinc-500 text-center pt-2">
        Existing active subscriptions are unaffected — they continue at the price they signed up at. New sign-ups pay the new amount.
      </div>
    </form>
  );
};

// ---------- Sub-component: A/B experiment ----------
const VariantCard = ({ name, variant, isLeader, onChange, disabled }) => (
  <div className={`bg-black/40 border rounded p-5 space-y-4 ${isLeader ? "border-emerald-600/60" : "border-zinc-700"}`}>
    <div className="flex items-center justify-between">
      <h3 className="text-base font-bold text-white tracking-tight inline-flex items-center gap-2">
        Variant {name}
        {isLeader && <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded"><Trophy className="w-3 h-3" /> Leading</span>}
      </h3>
      <span className="text-xs text-zinc-500 tabular-nums">{fmtMoney(variant.price, variant.currency)}/mo</span>
    </div>

    <div className="grid grid-cols-3 gap-3">
      <div className="col-span-2">
        <label className="text-[10px] uppercase tracking-wide text-zinc-500">Price</label>
        <input
          type="number" step="0.01" min="0.01" max="9999" disabled={disabled}
          value={variant.price}
          onChange={(e) => onChange({ ...variant, price: parseFloat(e.target.value) || 0 })}
          className="w-full mt-1 px-3 py-2 bg-zinc-900 border border-zinc-700 text-zinc-100 font-mono tabular-nums rounded text-sm disabled:opacity-60"
          data-testid={`exp-${name}-price`}
        />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wide text-zinc-500">Currency</label>
        <select
          value={variant.currency} disabled={disabled}
          onChange={(e) => onChange({ ...variant, currency: e.target.value })}
          className="w-full mt-1 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm disabled:opacity-60"
          data-testid={`exp-${name}-currency`}
        >
          {CURRENCIES.map((c) => <option key={c} value={c}>{c.toUpperCase()}</option>)}
        </select>
      </div>
    </div>

    <div>
      <label className="text-[10px] uppercase tracking-wide text-zinc-500">Badge label (overrides homepage badge)</label>
      <input
        value={variant.label || ""} maxLength={80} disabled={disabled}
        placeholder="e.g. Launch deal"
        onChange={(e) => onChange({ ...variant, label: e.target.value })}
        className="w-full mt-1 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-100 text-sm disabled:opacity-60"
        data-testid={`exp-${name}-label`}
      />
    </div>

    <div className="grid grid-cols-3 gap-3 border-t border-zinc-800 pt-4">
      <div>
        <p className="text-[10px] uppercase tracking-wide text-zinc-500">Visits</p>
        <p className="text-xl font-bold tabular-nums text-zinc-100" data-testid={`exp-${name}-visits`}>{variant.visits}</p>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wide text-zinc-500">Trial signups</p>
        <p className="text-xl font-bold tabular-nums text-zinc-100" data-testid={`exp-${name}-signups`}>{variant.signups}</p>
        <p className="text-[10px] text-zinc-500 tabular-nums">{pct(variant.signups, variant.visits)} conversion</p>
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wide text-zinc-500">Paid</p>
        <p className="text-xl font-bold tabular-nums text-emerald-400" data-testid={`exp-${name}-checkouts`}>{variant.checkouts}</p>
        <p className="text-[10px] text-zinc-500 tabular-nums">{pct(variant.checkouts, variant.signups)} signup→paid</p>
      </div>
    </div>
  </div>
);

const ExperimentTab = ({ data, onSaved }) => {
  const [enabled, setEnabled] = useState(!!data.ab_test_enabled);
  const [a, setA] = useState(data.variant_a);
  const [b, setB] = useState(data.variant_b);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Determine "leading" variant by paid-conversion rate (paid/visits) — if very few visits, no leader
  const totalVisits = (a.visits || 0) + (b.visits || 0);
  const aRate = a.visits > 0 ? a.checkouts / a.visits : 0;
  const bRate = b.visits > 0 ? b.checkouts / b.visits : 0;
  let leader = null;
  if (totalVisits >= 30 && Math.abs(aRate - bRate) > 0.0001) {
    leader = aRate > bRate ? "A" : "B";
  }

  const handleSave = async () => {
    if (!a.price || !b.price) { toast.error("Both variants need a price"); return; }
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/pricing-experiment`, {
        ab_test_enabled: enabled,
        variant_a: { price: a.price, currency: a.currency, label: a.label || "" },
        variant_b: { price: b.price, currency: b.currency, label: b.label || "" },
      });
      onSaved(r.data);
      setA(r.data.variant_a);
      setB(r.data.variant_b);
      toast.success(enabled ? "A/B test is live" : "A/B test paused — homepage now shows the base price");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset all A/B counters to zero? This does not change variant prices.")) return;
    setResetting(true);
    try {
      const r = await axios.post(`${API}/admin/pricing-experiment/reset`);
      onSaved(r.data);
      setA(r.data.variant_a);
      setB(r.data.variant_b);
      toast.success("Counters reset");
    } catch (e) {
      toast.error("Reset failed");
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-experiment-tab">
      <div className="bg-zinc-900 border border-zinc-800 rounded p-5 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-white inline-flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-[#002FA7]" /> A/B Pricing Test
          </h3>
          <p className="text-xs text-zinc-500 mt-1">
            Anonymous visitors are randomly shown variant A or B (50/50, sticky for 1 year).
            Conversion is tracked through trial sign-up and Stripe checkout.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <span className="text-xs text-zinc-400">{enabled ? "Live" : "Paused"}</span>
          <input
            type="checkbox" checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="w-5 h-5 accent-[#002FA7]"
            data-testid="exp-enabled-toggle"
          />
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <VariantCard name="A" variant={a} isLeader={leader === "A"} disabled={false} onChange={setA} />
        <VariantCard name="B" variant={b} isLeader={leader === "B"} disabled={false} onChange={setB} />
      </div>

      {!enabled && (
        <p className="text-[11px] text-zinc-500 italic">
          The experiment is paused — visitors see your base price. You can still edit variants here and save; flip the toggle on top to push them live.
        </p>
      )}

      {leader && (
        <div className="bg-emerald-950/30 border border-emerald-800/50 rounded p-3 text-xs text-emerald-300">
          <strong>Variant {leader}</strong> is currently leading on paid conversion (
          {pct(leader === "A" ? a.checkouts : b.checkouts, leader === "A" ? a.visits : b.visits)}{" "}
          vs{" "}
          {pct(leader === "A" ? b.checkouts : a.checkouts, leader === "A" ? b.visits : a.visits)}
          ). Once you have ≥200 visits per variant, consider promoting the winner via Save Base Price.
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={handleSave} disabled={saving}
          className="flex-1 py-2.5 bg-[#002FA7] hover:bg-[#00227A] text-white font-medium rounded inline-flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="exp-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : "Save experiment"}
        </button>
        <button
          onClick={handleReset} disabled={resetting}
          className="px-4 py-2.5 border border-zinc-700 hover:border-zinc-500 text-zinc-300 rounded inline-flex items-center gap-2 text-sm disabled:opacity-60"
          data-testid="exp-reset-btn"
        >
          {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Reset counters
        </button>
      </div>
    </div>
  );
};

// ---------- Main page ----------
const AdminPricing = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("base");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/subscription-pricing`);
      setData(r.data);
    } catch {
      toast.error("Failed to load pricing");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-pricing">
      <div>
        <h1 className="text-2xl font-bold text-white tracking-tight flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-[#002FA7]" /> Subscription Pricing
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Set the monthly subscription price and run A/B experiments to find the price your visitors convert best at.
        </p>
      </div>

      <div className="border-b border-zinc-800 flex gap-1">
        <TabButton active={tab === "base"} onClick={() => setTab("base")} icon={DollarSign} testid="tab-base">
          Base price
        </TabButton>
        <TabButton active={tab === "ab"} onClick={() => setTab("ab")} icon={FlaskConical} testid="tab-ab">
          A/B Experiment
        </TabButton>
      </div>

      {loading || !data ? (
        <div className="flex justify-center h-32 items-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" />
        </div>
      ) : tab === "base" ? (
        <BasePricingForm data={data} onSaved={setData} />
      ) : (
        <ExperimentTab data={data} onSaved={setData} />
      )}
    </div>
  );
};

export default AdminPricing;
